import { createSelector } from "@reduxjs/toolkit";
import { taxonomyApi } from "./api/productTaxonomyApi";
import { productApi } from "./api/productApi";
import { TransformedWPCategory } from "./types/productTaxonomyApiTypes";
import { RootState } from "../../../app/store";

// A new type to represent a category with its potential children
export interface HierarchicalCategory extends TransformedWPCategory {
  children: HierarchicalCategory[];
}

// Base selector to get the raw category data from the API endpoint
const selectCategoriesResult = taxonomyApi.endpoints.getCategories.select();

// The memoized selector that builds the category tree
export const selectCategoryTree = createSelector(
  [selectCategoriesResult], // Input selector
  (categoriesResult): HierarchicalCategory[] => {
    // Transformer function
    const categoriesById: Record<number, HierarchicalCategory> = {};
    const topLevelCategories: HierarchicalCategory[] = [];

    // Return an empty array if data isn't available yet
    if (!categoriesResult.data) {
      return [];
    }

    // Step A: Filter out empty categories and create a map for efficient lookups
    Object.values(categoriesResult.data.categories).forEach((category) => {
      if (category.count > 0) {
        categoriesById[category.id] = { ...category, children: [] };
      }
    });

    // Step B: Build the tree structure
    Object.values(categoriesById).forEach((category) => {
      if (category.parent !== 0 && categoriesById[category.parent]) {
        categoriesById[category.parent].children.push(category);
      } else {
        topLevelCategories.push(category);
      }
    });

    // Step C: Sort categories at each level by menu order
    const sortByMenuOrder = (
      a: HierarchicalCategory,
      b: HierarchicalCategory
    ) => a.menuOrder - b.menuOrder;

    topLevelCategories.sort(sortByMenuOrder);
    Object.values(categoriesById).forEach((category) => {
      if (category.children.length > 0) {
        category.children.sort(sortByMenuOrder);
      }
    });

    return topLevelCategories;
  }
);

// Input selector that gets just the filters object
const selectFilters = (state: RootState) => state.product.filters;

// The memoized selector that computes the boolean value
export const selectAreFiltersActive = createSelector(
  [selectFilters],
  (filters) =>
    filters.category.length > 0 ||
    filters.tag.length > 0 ||
    filters.brand.length > 0 ||
    filters.minPrice !== null ||
    filters.maxPrice !== null ||
    filters.search !== "" ||
    filters.onSale === true ||
    filters.inStock === true ||
    Object.keys(filters.attributes).length > 0
);

// 1. New, stable selector to get the collection data result for the current filters.
//    This replaces the original unstable `selectCollectionDataResult` function.
const selectCollectionDataResult = createSelector(
  [
    selectFilters, // This is a memoized input selector.
    (state: RootState) => state, // Pass the whole state through.
  ],
  (filters, state) => {
    // The result function only re-runs if `filters` or `state` reference changes.
    // `filters` is stable thanks to the input selector.

    // `select` is memoized. Given the same `filters` object, it returns the same selector instance.
    const selectDataForFilters =
      productApi.endpoints.getProductsCollectionData.select(filters);

    // We execute the memoized selector instance with the current state.
    return selectDataForFilters(state);
  }
);

// 2. Memoized selector to derive the currency symbol from the query data.
//    This now uses the new, stable `selectCollectionDataResult` as its input.
export const selectCurrencySymbol = createSelector(
  [selectCollectionDataResult],
  (collectionResult) => {
    // If data exists, return its symbol. Otherwise, provide a safe fallback.
    return collectionResult.data?.currencySymbol ?? "₺";
  }
);
